import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { fetchArenaLadders } from "@/api/rankingsApi";
import {
  ARENA_LADDER_OPTIONS,
  filterArenaEntries,
  findArenaLadder,
} from "./rankingsHelpers";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { 
  Trophy, 
  Swords, 
  Crown,
  Award,
  Search
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export default function Rankings() {
  const [nationalSearch, setNationalSearch] = useState("");
  const [arenaSearch, setArenaSearch] = useState("");
  const [clanSearch, setClanSearch] = useState("");
  const [arenaLadder, setArenaLadder] = useState("inter-spec");

  const { data: rankings = [], isLoading: isRankingsLoading } = useQuery({
    queryKey: ['rankings'],
    queryFn: async () => {
      const result = await base44.entities.Ranking.list('-national_points');
      return Array.isArray(result) ? result : [];
    },
  });

  const {
    data: arenaData,
    isLoading: isArenaLoading,
    isError: isArenaError,
  } = useQuery({
    queryKey: ['arena-ladders'],
    queryFn: ({ signal }) => fetchArenaLadders({ signal }),
    staleTime: 30_000,
  });

  const classColors = {
    Rogue: "bg-purple-900/20 text-purple-400 border-purple-400/30",
    Warlord: "bg-red-900/20 text-red-400 border-red-400/30",
    Cleric: "bg-yellow-900/20 text-yellow-400 border-yellow-400/30",
    Wizard: "bg-blue-900/20 text-blue-400 border-blue-400/30"
  };

  const classIcons = {
    Rogue: "🗡️",
    Warlord: "⚔️",
    Cleric: "✨",
    Wizard: "🔮"
  };

  const getRankBadge = (index) => {
    if (index === 0) return <Crown className="w-5 h-5 text-[#FFD700]" />;
    if (index === 1) return <Award className="w-5 h-5 text-[#C0C0C0]" />;
    if (index === 2) return <Award className="w-5 h-5 text-[#CD7F32]" />;
    return <span className="text-gray-500">{index + 1}</span>;
  };

  const nationalPointsRankings = [...rankings].sort((a, b) => b.national_points - a.national_points);
  const selectedArenaLadder = findArenaLadder(arenaData?.ladders, arenaLadder);

  // Group by clan for clan rankings
  const clanStats = {};
  rankings.forEach(player => {
    if (player.clan) {
      if (!clanStats[player.clan]) {
        clanStats[player.clan] = {
          name: player.clan,
          totalNP: 0,
          totalArena: 0,
          members: 0
        };
      }
      clanStats[player.clan].totalNP += player.national_points || 0;
      clanStats[player.clan].totalArena += player.arena_points || 0;
      clanStats[player.clan].members += 1;
    }
  });
  const clanRankings = Object.values(clanStats).sort((a, b) => b.totalNP - a.totalNP);

  // Filter functions
  const filteredNationalRankings = nationalPointsRankings.filter(player =>
    player.player_name.toLowerCase().includes(nationalSearch.toLowerCase())
  );

  const filteredArenaRankings = filterArenaEntries(selectedArenaLadder.entries, arenaSearch);

  const filteredClanRankings = clanRankings.filter(clan =>
    clan.name.toLowerCase().includes(clanSearch.toLowerCase())
  );

  return (
    <div className="min-h-screen relative">
      {/* Background - Fixed position */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#1a0a1a]/95 to-[#0a0a0a]" />
        <div className="absolute inset-0 bg-[url('https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/6915ad56fe64ad9662a36d0b/70e8d474a_loading_screen_02.png')] bg-cover bg-center opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/80 via-transparent to-[#0a0a0a]/90" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#0a0a0a_100%)]" />
      </div>

      {/* Content */}
      <div className="relative z-10 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero */}
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="text-white">
                Leaderboards
              </span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Prove your worth. Earn your legend. Only the best make it to the top.
            </p>
          </div>

          {/* Rankings Tabs */}
          <Tabs defaultValue="national" className="w-full">
            <TabsList className="grid w-full md:w-auto md:inline-grid grid-cols-3 bg-[#1a1a1a]/80 backdrop-blur-sm border border-[#8B0000]/30 mb-8">
              <TabsTrigger value="national" className="data-[state=active]:bg-[#8B0000] data-[state=active]:text-white">
                <Trophy className="w-4 h-4 mr-2" />
                National Points
              </TabsTrigger>
              <TabsTrigger value="arena" className="data-[state=active]:bg-[#8B0000] data-[state=active]:text-white">
                <Swords className="w-4 h-4 mr-2" />
                Arena
              </TabsTrigger>
              <TabsTrigger value="clans" className="data-[state=active]:bg-[#8B0000] data-[state=active]:text-white">
                <Crown className="w-4 h-4 mr-2" />
                Clans
              </TabsTrigger>
            </TabsList>

            {/* National Points Rankings */}
            <TabsContent value="national">
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    placeholder="Search by player name..."
                    value={nationalSearch}
                    onChange={(e) => setNationalSearch(e.target.value)}
                    className="pl-10 bg-[#0a0a0a] border-[#8B0000]/30 text-white focus:border-white"
                  />
                </div>
              </div>
              <Card className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30">
                {isRankingsLoading ? (
                  <div className="p-12 text-center">
                    <div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full mx-auto" />
                  </div>
                ) : filteredNationalRankings.length === 0 ? (
                  <div className="p-12 text-center">
                    <Trophy className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-400 mb-2">No results found</h3>
                    <p className="text-gray-500">Try a different search term</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#8B0000]/20 hover:bg-transparent">
                        <TableHead className="text-gray-400">Rank</TableHead>
                        <TableHead className="text-gray-400">Player</TableHead>
                        <TableHead className="text-gray-400">Class</TableHead>
                        <TableHead className="text-gray-400">Clan</TableHead>
                        <TableHead className="text-gray-400">Level</TableHead>
                        <TableHead className="text-gray-400 text-right">National Points</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredNationalRankings.map((player, index) => (
                        <TableRow 
                          key={player.id}
                          className={`border-[#8B0000]/20 hover:bg-[#8B0000]/10 ${
                            index < 3 ? 'bg-[#8B0000]/5' : ''
                          }`}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {getRankBadge(nationalPointsRankings.indexOf(player))}
                            </div>
                          </TableCell>
                          <TableCell className="font-semibold text-white">
                            {player.player_name}
                          </TableCell>
                          <TableCell>
                            <Badge className={`${classColors[player.class]} border`}>
                              <span className="mr-1">{classIcons[player.class]}</span>
                              {player.class}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-gray-400">
                            {player.clan || '-'}
                          </TableCell>
                          <TableCell className="text-gray-400">
                            {player.level}
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="text-white font-bold">
                              {player.national_points || 0}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>
            </TabsContent>

            {/* Arena Rankings */}
            <TabsContent value="arena">
              <Tabs value={arenaLadder} onValueChange={setArenaLadder} className="w-full">
                <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 bg-[#0a0a0a]/90 border border-[#8B0000]/30 mb-4">
                  {ARENA_LADDER_OPTIONS.map(ladder => (
                    <TabsTrigger
                      key={ladder.key}
                      value={ladder.key}
                      className="data-[state=active]:bg-[#8B0000] data-[state=active]:text-white"
                    >
                      {ladder.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    placeholder={`Search the ${selectedArenaLadder.label} ladder...`}
                    value={arenaSearch}
                    onChange={(e) => setArenaSearch(e.target.value)}
                    className="pl-10 bg-[#0a0a0a] border-[#8B0000]/30 text-white focus:border-white"
                  />
                </div>
              </div>
              <Card className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30">
                {isArenaLoading ? (
                  <div className="p-12 text-center">
                    <div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full mx-auto" />
                  </div>
                ) : isArenaError ? (
                  <div className="p-12 text-center">
                    <Swords className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-400 mb-2">Arena rankings unavailable</h3>
                    <p className="text-gray-500">Please try again in a moment.</p>
                  </div>
                ) : filteredArenaRankings.length === 0 ? (
                  <div className="p-12 text-center">
                    <Swords className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-400 mb-2">No rated players found</h3>
                    <p className="text-gray-500">
                      {arenaSearch ? "Try a different search term" : `The ${selectedArenaLadder.label} ladder is empty.`}
                    </p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#8B0000]/20 hover:bg-transparent">
                        <TableHead className="text-gray-400">Rank</TableHead>
                        <TableHead className="text-gray-400">Player</TableHead>
                        <TableHead className="text-gray-400">Class</TableHead>
                        <TableHead className="text-gray-400">Clan</TableHead>
                        <TableHead className="text-gray-400">Level</TableHead>
                        <TableHead className="text-gray-400">Rank Title</TableHead>
                        <TableHead className="text-gray-400 text-right">Rating</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredArenaRankings.map((player) => (
                        <TableRow 
                          key={`${selectedArenaLadder.key}-${player.characterId}`}
                          className={`border-[#8B0000]/20 hover:bg-[#8B0000]/10 ${
                            player.rank <= 3 ? 'bg-[#8B0000]/5' : ''
                          }`}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {getRankBadge(player.rank - 1)}
                            </div>
                          </TableCell>
                          <TableCell className="font-semibold text-white">
                            {player.playerName}
                          </TableCell>
                          <TableCell>
                            <Badge className={`${classColors[player.className] || "bg-gray-900/30 text-gray-300 border-gray-500/30"} border`}>
                              <span className="mr-1">{classIcons[player.className] || "?"}</span>
                              {player.className}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-gray-400">
                            {player.clan || '-'}
                          </TableCell>
                          <TableCell className="text-gray-400">
                            {player.level ?? '-'}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="border-[#8B0000]/40 text-gray-300">
                              {player.rankTitle}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="text-white font-bold">
                              {player.rating.toLocaleString()}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>
            </TabsContent>

            {/* Clan Rankings */}
            <TabsContent value="clans">
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    placeholder="Search by clan name..."
                    value={clanSearch}
                    onChange={(e) => setClanSearch(e.target.value)}
                    className="pl-10 bg-[#0a0a0a] border-[#8B0000]/30 text-white focus:border-white"
                  />
                </div>
              </div>
              <Card className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30">
                {filteredClanRankings.length === 0 ? (
                  <div className="p-12 text-center">
                    <Crown className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-400 mb-2">No results found</h3>
                    <p className="text-gray-500">Try a different search term</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#8B0000]/20 hover:bg-transparent">
                        <TableHead className="text-gray-400">Rank</TableHead>
                        <TableHead className="text-gray-400">Clan Name</TableHead>
                        <TableHead className="text-gray-400">Members</TableHead>
                        <TableHead className="text-gray-400 text-right">Total NP</TableHead>
                        <TableHead className="text-gray-400 text-right">Total Arena</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredClanRankings.map((clan, index) => (
                        <TableRow 
                          key={clan.name}
                          className={`border-[#8B0000]/20 hover:bg-[#8B0000]/10 ${
                            index < 3 ? 'bg-[#8B0000]/5' : ''
                          }`}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {getRankBadge(clanRankings.indexOf(clan))}
                            </div>
                          </TableCell>
                          <TableCell className="font-semibold text-white text-lg">
                            {clan.name}
                          </TableCell>
                          <TableCell className="text-gray-400">
                            {clan.members} members
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="text-white font-bold">
                              {clan.totalNP}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="text-white font-bold">
                              {clan.totalArena}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>
            </TabsContent>
          </Tabs>

          {/* Bottom Info */}
          <div className="mt-12 text-center">
            <p className="text-gray-500 text-sm">
              Rankings update in real-time. Compete, improve, and claim your place among legends.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
